interface Series { data: number[] }

interface LineChartProps {
  series: Series[]
  labels: string[]
  height?: number
}

export default function LineChart({ series, labels, height = 240 }: LineChartProps) {
  const w = 760, h = height
  const padL = 36, padR = 16, padT = 18, padB = 28
  const allVals = series.flatMap(s => s.data)
  const max = Math.max(...allVals) * 1.15
  const min = 0
  const xStep = (w - padL - padR) / (labels.length - 1)
  const yScale = (v: number) => h - padB - ((v - min) / (max - min)) * (h - padT - padB)

  const grid = []
  for (let i = 0; i < 5; i++) {
    const y = padT + (i * (h - padT - padB)) / 4
    const val = Math.round((max - (i * (max - min)) / 4) / 10) * 10
    grid.push(
      <g key={i}>
        <line className="chart-grid" x1={padL} x2={w - padR} y1={y} y2={y} />
        <text className="chart-axis" x={padL - 8} y={y + 3} textAnchor="end">{val}</text>
      </g>
    )
  }

  return (
    <svg className="chart-svg" viewBox={`0 0 ${w} ${h}`}>
      <defs>
        <linearGradient id="chartGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(15,42,74,.18)" />
          <stop offset="100%" stopColor="rgba(15,42,74,0)" />
        </linearGradient>
        <linearGradient id="chartGrad2" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="rgba(124,179,66,.25)" />
          <stop offset="100%" stopColor="rgba(124,179,66,0)" />
        </linearGradient>
      </defs>
      {grid}
      {labels.map((l, i) => (
        <text key={i} className="chart-axis" x={padL + i * xStep} y={h - padB + 18} textAnchor="middle">{l}</text>
      ))}
      {series.map((s, sIdx) => {
        const pts = s.data.map((v, i): [number, number] => [padL + i * xStep, yScale(v)])
        const dLine = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ')
        const dArea = dLine + ` L ${pts[pts.length - 1][0]} ${h - padB} L ${pts[0][0]} ${h - padB} Z`
        return (
          <g key={sIdx}>
            <path d={dArea} fill={`url(#chartGrad${sIdx === 1 ? '2' : ''})`} />
            <path className={`chart-line${sIdx === 1 ? ' chart-line-2' : ''}`} d={dLine} />
            {pts.map((p, i) => (
              <circle key={i} className="chart-dot" cx={p[0]} cy={p[1]} r="4"
                style={sIdx === 1 ? { stroke: 'var(--green-500)' } : undefined} />
            ))}
          </g>
        )
      })}
    </svg>
  )
}
